import { useEffect } from 'react';
import { announceEmbeddedReady } from '@/lib/platformEmbed';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toast } from '@heroui/react';
import { queryClient } from '@/lib/queryClient';
import { HomePage } from '@/pages/HomePage';
import { LoginErrorPage } from '@/pages/LoginErrorPage';
import { PublicProfilePage } from '@/pages/PublicProfilePage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { TermsPage } from '@/pages/TermsPage';
import { TooltipProvider } from '@/components/Tooltip';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { DesktopUpdatePrompt } from '@/components/DesktopUpdatePrompt';
import { DmToastProvider } from '@/lib/dmToastQueue';
import { useIsNarrowViewport } from '@/lib/useIsNarrowViewport';

export function App() {
  const narrowViewport = useIsNarrowViewport();
  // The host needs this before the account gate can open. A hidden frame must
  // not wait for RAF; announceEmbeddedReady is idempotent under StrictMode.
  useEffect(() => { announceEmbeddedReady(); }, []);
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* HeroUI v3 is CSS-first (styles come from `@import "@heroui/styles"`)
              and needs no app-level provider — its components are self-contained
              React-Aria primitives. Light/dark is driven by the `.light`/`.dark`
              class applyTheme() keeps on <html>. */}
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login-error" element={<LoginErrorPage />} />
              {/* Public share/QR landing — must render without a session. */}
              <Route path="/u/:username" element={<PublicProfilePage />} />
              {/* Store requirement: a policy URL a reviewer can open in a plain
                  browser, with no session and no app installed. */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
        {/* HeroUI native toast layer. Variants (success / danger / warning /
            accent) inherit the design tokens from HeroUI's own styles, so no
            CSS-var wiring is needed; light/dark follows the html theme class.
            All `toast.*` calls route here through the @/lib/toast adapter. */}
        <Toast.Provider
          placement={narrowViewport ? 'top end' : 'bottom end'}
          maxVisibleToasts={narrowViewport ? 1 : 5}
          className={narrowViewport ? 'app-compact-toasts' : undefined}
        />
        {/* In-app DM toast region (top-right): "кто пишет + превью" cards for
            incoming direct/group-DM messages while you're elsewhere in Bunker. */}
        <DmToastProvider />
        {/* Desktop-only: in-app update prompt (no-op in a browser). */}
        <DesktopUpdatePrompt />
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
